import i18n from "../language/i18n";
import { filterGradeLevelOptionList } from "./ManagementUtilization";
import {
  buildCategoryKey,
  buildCategoryParentKey,
} from "./CategoryKeyUtilization";

export const retrieveCurriculumTypeOptionList = () => {
  return [
    {
      value: "MEB",
      text: i18n.t("MEB"),
    },
    {
      value: "AP",
      text: i18n.t("AP"),
    },
    {
      value: "IB",
      text: i18n.t("IB"),
    },
    {
      value: "DFE",
      text: i18n.t("DFE"),
    },
  ];
};

export const filterCurriculumTypeOptionList = (gradeDTO: any) => {
  if (gradeDTO) {
    return retrieveCurriculumTypeOptionList().filter(
      (curriculumTypeOption) =>
        curriculumTypeOption.value === gradeDTO.curriculumType,
    );
  } else {
    return retrieveCurriculumTypeOptionList();
  }
};

/**
 * The curriculum version codes offered per subject + grade.
 *
 * 🚨 MEB year rule — grades 4, 8 and 12 are still on the legacy 2018/2019
 * program; every other grade has moved to the Türkiye Yüzyılı Maarif Modeli
 * (2024) one. The two bands PARTITION the grades, they do not overlap: a legacy
 * code is offered ONLY at 4, 8 and 12, and a 2024 code ONLY outside them.
 * Whenever a grade moves between the bands, the receiving code must be widened
 * to cover it in the SAME edit — otherwise the learner is left with a subject
 * that has no curriculum at all.
 *
 * The rule constrains the 2018 codes only where a 2024 successor actually covers
 * the vacated grade. Two subjects still have no 2024 curriculum seeded at all
 * (DKAB in both bands, Hayat Bilgisi), so there the 2018 program keeps every
 * grade it had. The three that used to be listed here no longer belong:
 * catchup-parent V20260912_0 seeds MEB-IO-TUR-2024 at grade 7, MEB-AL-TDE-2024
 * at grade 11, and a new MEB-IO-ENG-2024 for İngilizce.
 * For those, the 2018 program is not legacy — it is the only program there is.
 * Each is marked `year rule exception` below.
 *
 * The mirror case: a 2024 code whose subject is taught ONLY at a terminal grade
 * has nowhere to be offered at all. İnkılap Tarihi is a grade-8 (İO) and
 * grade-12 (AL) subject, so MEB-IO-ITA-2024 and MEB-AL-ITA-2024 both carry an
 * empty list until TYMM reaches those grades — like MEB-AL-FEL-2018 in the other
 * direction, the row stays because versionCode -> ders resolution still reads it
 * for already-authored content.
 *
 * ⚠️ This catalog is duplicated in catchup-admin-web (src/utilization) and
 * catchup-mobile (src/utilization) — the three drifted apart once already, each
 * frozen at a different rollout year. Change all of them together.
 *
 * 🚧 As of the year-rule change this web and catchup-admin-web are in step;
 * **catchup-mobile is deliberately NOT and is now the copy that is behind** — it
 * still offers the 2018 codes at grades 5-7 and 9-11. Until it is caught up, the
 * rule above describes two of the three catalogs, and mobile is the drift this
 * warning exists to prevent.
 */
/**
 * The grades the 2024 curriculum has not reached — the terminal grades of the MEB
 * year rule documented on `retrieveCategoryVersionCodeOptionList` below. They are
 * the only grades that still offer a legacy code, and therefore the only grades
 * where one subject can present TWO bands at once.
 */
export const TERMINAL_CURRICULUM_LEVEL_LIST = [4, 8, 12];

/** True for a legacy (pre-2024) MEB curriculum version code — `…-2018` / `…-2019`. */
export const checkLegacyCurriculumVersionCode = (versionCode: string) =>
  /-(2018|2019)$/.test(versionCode || "");

/**
 * Which band to fall back to among already level-filtered candidates when NOTHING
 * about the learner disambiguates them.
 *
 * 🚨 Catalog order is not a preference. Every subject lists its 2024 code before its
 * legacy one, so "take the first candidate" silently means "take 2024" — and at a
 * terminal grade that is the band that grade has NOT transitioned to. Before the year
 * rule those grades had a single candidate, so the arbitrary pick was correct by
 * construction; now they have two and the tie has to be broken on the rule instead.
 *
 * Only the no-information case comes here. Once the learner's plan covers a band, that
 * coverage is real evidence and is preferred over this — even at a terminal grade.
 * Level-agnostic families (CEFR A1→B2) have no legacy code, so they keep catalog order,
 * which for them IS the pedagogical order.
 */
export const retrieveDefaultCategoryVersionCodeOption = (
  candidateOptionList: any,
  level?: any,
) => {
  const optionList = candidateOptionList || [];
  if (optionList.length === 0) return null;
  if (!TERMINAL_CURRICULUM_LEVEL_LIST.includes(level)) return optionList[0];
  return (
    optionList.find((option: any) =>
      checkLegacyCurriculumVersionCode(option?.value),
    ) || optionList[0]
  );
};

export const retrieveCategoryVersionCodeOptionList = (
  curriculumType: string,
) => {
  if (curriculumType === "MEB") {
    return [
      {
        value: "MEB-IO-MAT-2024",
        text: i18n.t("MEB-IO-MAT-2024"),
        type: "MATHEMATICS",
        availableLevelList: [5, 6, 7],
      },
      {
        value: "MEB-IO-MAT-2018",
        text: i18n.t("MEB-IO-MAT-2018"),
        type: "MATHEMATICS",
        availableLevelList: [4, 8],
      },
      {
        value: "MEB-IO-TUR-2024",
        text: i18n.t("MEB-IO-TUR-2024"),
        type: "TURKISH",
        availableLevelList: [5, 6, 7],
      },
      {
        value: "MEB-IO-TUR-2019",
        text: i18n.t("MEB-IO-TUR-2019"),
        type: "TURKISH",
        availableLevelList: [4, 8],
      },
      // year rule exception: Hayat Bilgisi (1-3) has no 2024 code at all.
      {
        value: "MEB-IO-HAY-2018",
        text: i18n.t("MEB-IO-HAY-2018"),
        type: "LIFE_STUDIES",
        availableLevelList: [3],
      },
      {
        value: "MEB-IO-TRAF-2018",
        text: i18n.t("MEB-IO-TRAF-2018"),
        type: "TRAFFIC_SAFETY",
        availableLevelList: [4],
      },
      {
        value: "MEB-IO-FEN-2024",
        text: i18n.t("MEB-IO-FEN-2024"),
        type: "SCIENCE",
        availableLevelList: [5, 6, 7],
      },
      {
        value: "MEB-IO-SCI-2018",
        text: i18n.t("MEB-IO-SCI-2018"),
        type: "SCIENCE",
        availableLevelList: [4, 8],
      },
      {
        value: "MEB-IO-SOS-2024",
        text: i18n.t("MEB-IO-SOS-2024"),
        type: "SOCIAL_STUDIES",
        availableLevelList: [5, 6, 7],
      },
      {
        value: "MEB-IO-SOS-2018",
        text: i18n.t("MEB-IO-SOS-2018"),
        type: "SOCIAL_STUDIES",
        availableLevelList: [4],
      },
      {
        value: "MEB-IO-ITA-2018",
        text: i18n.t("MEB-IO-ITA-2018"),
        type: "SOCIAL_STUDIES",
        availableLevelList: [8],
      },
      {
        value: "MEB-IO-ITA-2024",
        text: i18n.t("MEB-IO-ITA-2024"),
        type: "SOCIAL_STUDIES",
        availableLevelList: [],
      },
      // Maarif covers grades 1, 2, 3, 5, 6, 7, 9, 10 and 11 in 2026-2027, so the 2024 catalogues are
      // offered only there; the 2018 one keeps grades 4 and 8. Both catalogues carry every grade MEB
      // publishes (ENG-2024 also has grades 2, 3 and 8, ALM-2024 grade 8) — this list, not the seed,
      // decides what a teacher sees. English is not offered below grade 4, so its grade-2 and
      // grade-3 rows are seeded but never reachable; Hayat Bilgisi does run at grade 3, so this is
      // a per-subject scope, not a platform floor. Switching a grade over later is one line here.
      {
        value: "MEB-IO-ENG-2024",
        text: i18n.t("MEB-IO-ENG-2024"),
        type: "ENGLISH",
        availableLevelList: [5, 6, 7],
      },
      {
        value: "MEB-IO-ENG-2018",
        text: i18n.t("MEB-IO-ENG-2018"),
        type: "ENGLISH",
        availableLevelList: [4, 8],
      },
      {
        value: "MEB-IO-ALM-2024",
        text: i18n.t("MEB-IO-ALM-2024"),
        type: "GERMAN",
        availableLevelList: [5, 6, 7],
      },
      // CEFR is a proficiency scale, not a grade-bound curriculum: its category
      // rows are stored level-agnostic (level 0) and matched by versionCode alone.
      // We list every band for all English grades (4-12) so it is reachable from any
      // English class; the learning path shows one band at a time and defaults to the
      // learner's active band (see LearningPathScreen `preferredVersionCode`).
      {
        value: "CEFR-STANDARDS-A1",
        text: i18n.t("CEFR-STANDARDS-A1"),
        type: "ENGLISH",
        availableLevelList: [4, 5, 6, 7, 8, 9, 10, 11, 12],
      },
      {
        value: "CEFR-STANDARDS-A2",
        text: i18n.t("CEFR-STANDARDS-A2"),
        type: "ENGLISH",
        availableLevelList: [4, 5, 6, 7, 8, 9, 10, 11, 12],
      },
      {
        value: "CEFR-STANDARDS-B1",
        text: i18n.t("CEFR-STANDARDS-B1"),
        type: "ENGLISH",
        availableLevelList: [4, 5, 6, 7, 8, 9, 10, 11, 12],
      },
      {
        value: "CEFR-STANDARDS-B2",
        text: i18n.t("CEFR-STANDARDS-B2"),
        type: "ENGLISH",
        availableLevelList: [4, 5, 6, 7, 8, 9, 10, 11, 12],
      },
      // German CEFR (CEFR-DE-*): typed coterie GERMAN, same level-agnostic band
      // matching as English. A1-B2 seeded; C1/C2 pending descriptor sourcing.
      {
        value: "CEFR-DE-STANDARDS-A1",
        text: i18n.t("CEFR-DE-STANDARDS-A1"),
        type: "GERMAN",
        availableLevelList: [4, 5, 6, 7, 8, 9, 10, 11, 12],
      },
      {
        value: "CEFR-DE-STANDARDS-A2",
        text: i18n.t("CEFR-DE-STANDARDS-A2"),
        type: "GERMAN",
        availableLevelList: [4, 5, 6, 7, 8, 9, 10, 11, 12],
      },
      {
        value: "CEFR-DE-STANDARDS-B1",
        text: i18n.t("CEFR-DE-STANDARDS-B1"),
        type: "GERMAN",
        availableLevelList: [4, 5, 6, 7, 8, 9, 10, 11, 12],
      },
      {
        value: "CEFR-DE-STANDARDS-B2",
        text: i18n.t("CEFR-DE-STANDARDS-B2"),
        type: "GERMAN",
        availableLevelList: [4, 5, 6, 7, 8, 9, 10, 11, 12],
      },
      // French (SPE-FRA-*): a school-authored catalogue (BILFEN Liseleri), not a MEB
      // or CoE one, hosted under curriculumType MEB the way SPE-TYT-TUR-2024 is. Typed
      // coterie FRENCH, which is what routes the AI pipeline to French. Level-agnostic
      // like the CEFR ladders (rows stored level 0; the band lives in the code), listed
      // for 9-12 because the brand teaches it in lise only.
      {
        value: "SPE-FRA-A1-2026",
        text: i18n.t("SPE-FRA-A1-2026"),
        type: "FRENCH",
        availableLevelList: [9, 10, 11, 12],
      },
      // ZAFER X (SPE-ZFX-*): Zafer's own AI course, one brand only. Grouped by LEVEL
      // (Pre-Explorer … Innovator II), not by grade — grades 5-12 study the same outcomes —
      // so its rows are stored at grade 0 and listed there alone; the grade filters below
      // let a grade-free code through, since the learner's own grade is never 0.
      {
        value: "SPE-ZFX-2026",
        text: i18n.t("SPE-ZFX-2026"),
        type: "ZAFER_X",
        availableLevelList: [0],
      },
      // German (SPE-DEU-*): the schools' own German programmes, one catalogue per CEFR
      // band (A2.1 + A2.2 = A2 by PO decision). Same contract as SPE-FRA-*: typed coterie
      // GERMAN, rows stored level 0, band in the version code and the outcome code
      // (DEU.B1.21.3.), listed for lise grades 9-12.
      {
        value: "SPE-DEU-A2-2026",
        text: i18n.t("SPE-DEU-A2-2026"),
        type: "GERMAN",
        availableLevelList: [9, 10, 11, 12],
      },
      {
        value: "SPE-DEU-B1-2026",
        text: i18n.t("SPE-DEU-B1-2026"),
        type: "GERMAN",
        availableLevelList: [9, 10, 11, 12],
      },
      {
        value: "SPE-DEU-B2-2026",
        text: i18n.t("SPE-DEU-B2-2026"),
        type: "GERMAN",
        availableLevelList: [9, 10, 11, 12],
      },
      {
        value: "MEB-AL-MAT-2024",
        text: i18n.t("MEB-AL-MAT-2024"),
        type: "MATHEMATICS",
        availableLevelList: [9, 10, 11],
      },
      {
        value: "MEB-AL-MAT-2018",
        text: i18n.t("MEB-AL-MAT-2018"),
        type: "MATHEMATICS",
        availableLevelList: [12],
      },
      {
        value: "MEB-AL-FİZ-2024",
        text: i18n.t("MEB-AL-FİZ-2024"),
        type: "PHYSICS",
        availableLevelList: [9, 10, 11],
      },
      {
        value: "MEB-AL-FIZ-2018",
        text: i18n.t("MEB-AL-FIZ-2018"),
        type: "PHYSICS",
        availableLevelList: [12],
      },
      {
        value: "MEB-AL-BİY-2024",
        text: i18n.t("MEB-AL-BİY-2024"),
        type: "BIOLOGY",
        availableLevelList: [9, 10, 11],
      },
      {
        value: "MEB-AL-BIO-2018",
        text: i18n.t("MEB-AL-BIO-2018"),
        type: "BIOLOGY",
        availableLevelList: [12],
      },
      {
        value: "MEB-AL-KİM-2024",
        text: i18n.t("MEB-AL-KİM-2024"),
        type: "CHEMISTRY",
        availableLevelList: [9, 10, 11],
      },
      {
        value: "MEB-AL-KIM-2018",
        text: i18n.t("MEB-AL-KIM-2018"),
        type: "CHEMISTRY",
        availableLevelList: [12],
      },
      {
        value: "MEB-AL-TAR-2024",
        text: i18n.t("MEB-AL-TAR-2024"),
        type: "HISTORY",
        availableLevelList: [9, 10, 11],
      },
      {
        value: "MEB-AL-TAR-2018",
        text: i18n.t("MEB-AL-TAR-2018"),
        type: "HISTORY",
        availableLevelList: [12],
      },
      {
        value: "MEB-AL-ITA-2018",
        text: i18n.t("MEB-AL-ITA-2018"),
        type: "HISTORY",
        availableLevelList: [12],
      },
      {
        value: "MEB-AL-ITA-2024",
        text: i18n.t("MEB-AL-ITA-2024"),
        type: "HISTORY",
        availableLevelList: [],
      },
      {
        value: "MEB-AL-COĞ-2024",
        text: i18n.t("MEB-AL-COĞ-2024"),
        type: "GEOGRAPHY",
        availableLevelList: [9, 10, 11],
      },
      {
        value: "MEB-AL-COĞ-2018",
        text: i18n.t("MEB-AL-COĞ-2018"),
        type: "GEOGRAPHY",
        availableLevelList: [12],
      },
      // year rule exception: no 2024 DKAB code in either band.
      {
        value: "MEB-IO-DKAB-2018",
        text: i18n.t("MEB-IO-DKAB-2018"),
        type: "CULTURE_AND_RELIGION_KNOWLEDGE",
        availableLevelList: [4, 5, 6, 7, 8],
      },
      {
        value: "MEB-AL-DKAB-2018",
        text: i18n.t("MEB-AL-DKAB-2018"),
        type: "CULTURE_AND_RELIGION_KNOWLEDGE",
        availableLevelList: [9, 10, 11, 12],
      },
      {
        value: "MEB-AL-FEL-2024",
        text: i18n.t("MEB-AL-FEL-2024"),
        type: "PHILOSOPHY",
        availableLevelList: [10, 11],
      },
      // Fully superseded by MEB-AL-FEL-2024: Felsefe is a 10-11 subject, so the year
      // rule leaves it no grade. The row stays for versionCode -> subject resolution of
      // already-authored content; an empty list offers it nowhere.
      {
        value: "MEB-AL-FEL-2018",
        text: i18n.t("MEB-AL-FEL-2018"),
        type: "PHILOSOPHY",
        availableLevelList: [],
      },
      // Grade 11 moved to the 2024 programme in 2026-2027, so the 2018 catalogue keeps only the
      // terminal grade 12. What grade 11 may pick is decided HERE; the rows behind it come from
      // the checked-in bundle below, regenerated in this same change.
      {
        value: "MEB-AL-TDE-2024",
        text: i18n.t("MEB-AL-TDE-2024"),
        type: "LITERATURE",
        availableLevelList: [9, 10, 11],
      },
      {
        value: "MEB-AL-LIT-2018",
        text: i18n.t("MEB-AL-LIT-2018"),
        type: "LITERATURE",
        availableLevelList: [12],
      },
      // Lise İngilizce follows the same year rule: the Maarif catalogue carries grade 12 too, but
      // MEB's 2026-2027 plan still teaches grade 12 on the 2018 programme.
      {
        value: "MEB-AL-ENG-2024",
        text: i18n.t("MEB-AL-ENG-2024"),
        type: "ENGLISH",
        availableLevelList: [9, 10, 11],
      },
      {
        value: "MEB-AL-ENG-2018",
        text: i18n.t("MEB-AL-ENG-2018"),
        type: "ENGLISH",
        availableLevelList: [12],
      },
      {
        value: "GENERAL-CULTURE",
        text: i18n.t("GENERAL-CULTURE"),
        type: "GENERAL_CULTURE",
        availableLevelList: [8, 12],
      },
      {
        value: "SPE-TYT-TUR-2024",
        text: i18n.t("SPE-TYT-TUR-2024"),
        type: "TURKISH",
        availableLevelList: [12],
      },
      {
        value: "SPE-LGS-TUR-2024",
        text: i18n.t("SPE-LGS-TUR-2024"),
        type: "TURKISH",
        availableLevelList: [8],
      },
    ];
  } else if (curriculumType === "AP") {
    return [
      {
        value: "AP-CHEM",
        text: i18n.t("AP-CHEM"),
        type: "CHEMISTRY",
        availableLevelList: [9],
      },
    ];
  } else if (curriculumType === "IB") {
    // IB (International Baccalaureate) — generation-scope scaffold. Programme is encoded in the
    // versionCode (IB-DP-… / IB-MYP-… / IB-PYP-…). Outcomes are seeded as Category trees per
    // plans/ib-generation.md §4; this catalog row must match the seed migration's version_code +
    // level. Pilot: DP Biology (confirm coterieType/level when the seed lands).
    return [
      {
        value: "IB-DP-BIO-2025",
        text: i18n.t("IB-DP-BIO-2025"),
        type: "BIOLOGY",
        availableLevelList: [11, 12],
      },
      {
        value: "IB-DP-CHE-2025",
        text: i18n.t("IB-DP-CHE-2025"),
        type: "CHEMISTRY",
        availableLevelList: [11, 12],
      },
      {
        value: "IB-DP-PHY-2025",
        text: i18n.t("IB-DP-PHY-2025"),
        type: "PHYSICS",
        availableLevelList: [11, 12],
      },
      {
        value: "IB-DP-MAA-2025",
        text: i18n.t("IB-DP-MAA-2025"),
        type: "MATHEMATICS",
        availableLevelList: [11, 12],
      },
      {
        value: "IB-DP-MAI-2025",
        text: i18n.t("IB-DP-MAI-2025"),
        type: "MATHEMATICS",
        availableLevelList: [11, 12],
      },
      {
        value: "IB-MYP-SCI-2025",
        text: i18n.t("IB-MYP-SCI-2025"),
        type: "SCIENCE",
        availableLevelList: [6, 7, 8, 9, 10],
      },
      {
        value: "IB-MYP-LAL-2025",
        text: i18n.t("IB-MYP-LAL-2025"),
        type: "LITERATURE",
        availableLevelList: [6, 7, 8, 9, 10],
      },
      {
        value: "IB-MYP-LAC-2025",
        text: i18n.t("IB-MYP-LAC-2025"),
        type: "FOREIGN_LANGUAGE",
        availableLevelList: [6, 7, 8, 9, 10],
      },
      {
        value: "IB-MYP-INS-2025",
        text: i18n.t("IB-MYP-INS-2025"),
        type: "SOCIAL_STUDIES",
        availableLevelList: [6, 7, 8, 9, 10],
      },
      {
        value: "IB-MYP-MAT-2025",
        text: i18n.t("IB-MYP-MAT-2025"),
        type: "MATHEMATICS",
        availableLevelList: [6, 7, 8, 9, 10],
      },
      {
        value: "IB-MYP-ART-2025",
        text: i18n.t("IB-MYP-ART-2025"),
        type: "ARTS",
        availableLevelList: [6, 7, 8, 9, 10],
      },
      {
        value: "IB-MYP-PHE-2025",
        text: i18n.t("IB-MYP-PHE-2025"),
        type: "PHYSICAL_EDUCATION",
        availableLevelList: [6, 7, 8, 9, 10],
      },
      {
        value: "IB-MYP-DES-2025",
        text: i18n.t("IB-MYP-DES-2025"),
        type: "DESIGN",
        availableLevelList: [6, 7, 8, 9, 10],
      },
    ];
  } else if (curriculumType === "DFE") {
    // England national curriculum (DfE) science, one catalogue per key stage; level = school Year.
    // Must match admin-web and V20261027_0 in catchup-parent.
    return [
      {
        value: "ENG-NC-KS2-SCI-2014",
        text: i18n.t("ENG-NC-KS2-SCI-2014"),
        type: "SCIENCE",
        availableLevelList: [3, 4, 5, 6],
      },
      {
        value: "ENG-NC-KS3-SCI-2014",
        text: i18n.t("ENG-NC-KS3-SCI-2014"),
        type: "SCIENCE",
        availableLevelList: [7, 8, 9],
      },
      {
        value: "ENG-NC-KS4-SCI-2014",
        text: i18n.t("ENG-NC-KS4-SCI-2014"),
        type: "SCIENCE",
        availableLevelList: [10, 11],
      },
    ];
  } else {
    return [];
  }
};

/**
 * Lessons whose catalogue is not tied to a grade: their outcomes are stored at grade 0 and taught
 * at every grade (today ZAFER X, Zafer's AI course, grades 5-12 grouped by level). A learner's
 * grade is never 0, so every grade filter over version codes lets these through.
 */
export const isGradeFreeCoterieType = (coterieType: any): boolean =>
  coterieType === "ZAFER_X";

export const filterCategoryVersionCodeOptionList = (
  categoryVersionCodeOptionList: any,
  coterieTypeOrTypes: string | string[],
  level?: any,
) => {
  const coterieTypes = Array.isArray(coterieTypeOrTypes)
    ? coterieTypeOrTypes
    : [coterieTypeOrTypes];

  if (coterieTypes.some((type) => type === "DEFAULT_OPTION")) return [];
  if (level && level === "DEFAULT_OPTION") return [];

  let currentCategoryVersionCodeOptionList = categoryVersionCodeOptionList;

  if (!coterieTypes.includes("MANAGEMENT")) {
    currentCategoryVersionCodeOptionList =
      currentCategoryVersionCodeOptionList.filter((categoryVersionCode: any) =>
        coterieTypes.includes(categoryVersionCode.type),
      );
  }

  if (level) {
    currentCategoryVersionCodeOptionList =
      currentCategoryVersionCodeOptionList.filter(
        (categoryVersionCode: any) =>
          isGradeFreeCoterieType(categoryVersionCode.type) ||
          categoryVersionCode.availableLevelList.includes(level),
      );
  }

  return currentCategoryVersionCodeOptionList;
};

export const filterCategoryVersionCodeOptionListByGradeDTO = (
  categoryVersionCodeOptionList: any,
  coterieType: string,
  gradeDTO: any,
) => {
  if (coterieType && coterieType === "DEFAULT_OPTION") return [];

  let currentCategoryVersionCodeOptionList = categoryVersionCodeOptionList;

  if (coterieType !== "MANAGEMENT") {
    currentCategoryVersionCodeOptionList =
      currentCategoryVersionCodeOptionList.filter(
        (categoryVersionCode: any) => categoryVersionCode.type === coterieType,
      );
  }
  if (gradeDTO) {
    currentCategoryVersionCodeOptionList =
      currentCategoryVersionCodeOptionList.filter(
        (categoryVersionCodeOption: any) =>
          isGradeFreeCoterieType(categoryVersionCodeOption.type) ||
          categoryVersionCodeOption.availableLevelList.includes(gradeDTO.level),
      );
  }

  return currentCategoryVersionCodeOptionList;
};

export const filterCategoryVersionCodeOptionListByInstitutionDTO = (
  categoryVersionCodeOptionList: any,
  coterieType: string,
  institutionDTO: any,
) => {
  if (coterieType && coterieType === "DEFAULT_OPTION") return [];

  let currentCategoryVersionCodeOptionList = categoryVersionCodeOptionList;

  if (coterieType !== "MANAGEMENT") {
    currentCategoryVersionCodeOptionList =
      currentCategoryVersionCodeOptionList.filter(
        (categoryVersionCodeOption: any) =>
          categoryVersionCodeOption.type === coterieType,
      );
  }
  if (institutionDTO) {
    const gradeLevelList = filterGradeLevelOptionList(institutionDTO, null).map(
      (option) => option.value,
    );
    currentCategoryVersionCodeOptionList =
      currentCategoryVersionCodeOptionList.filter(
        (categoryVersionCodeOption: any) => {
          if (isGradeFreeCoterieType(categoryVersionCodeOption.type)) return true;
          return (
            gradeLevelList.find((gradeLevel) =>
              categoryVersionCodeOption.availableLevelList.includes(gradeLevel),
            ) !== undefined
          );
        },
      );
  }

  return currentCategoryVersionCodeOptionList;
};

export const retrieveSkillVersionCodeOptionList = (curriculumType) => {
  if (curriculumType === "IB") {
    // IB ATL (Approaches to Learning) — one cross-programme skill framework
    // shared by DP and MYP (versionCode IB-ATL-2025). type:null => offered for
    // every coterie, matching ATL's cross-subject nature.
    return [{ value: "IB-ATL-2025", text: i18n.t("IB-ATL-2025"), type: null }];
  }
  if (curriculumType === "DFE") {
    // The England national curriculum has no skill framework; never fall back to the MEB ones.
    return [];
  }
  return [
    {
      value: "MEB-IOL-KB-2024",
      text: i18n.t("MEB-IOL-KB-2024"),
      type: null,
    },
    {
      value: "MEB-IOL-E-2024",
      text: i18n.t("MEB-IOL-E-2024"),
      type: null,
    },
    {
      value: "MEB-IOL-MAB-2024",
      text: i18n.t("MEB-IOL-MAB-2024"),
      type: "MATHEMATICS",
    },
    {
      value: "MEB-IOL-TAB-2024",
      text: i18n.t("MEB-IOL-TAB-2024"),
      type: "TURKISH",
    },
    {
      value: "MEB-IOL-FBAB-2024",
      text: i18n.t("MEB-IOL-FBAB-2024"),
      type: "SCIENCE",
    },
    {
      value: "MEB-IOL-SBAB-2024",
      text: i18n.t("MEB-IOL-SBAB-2024"),
      type: "SOCIAL_STUDIES",
    },
    {
      value: "MEB-IOL-SAB-2024",
      text: i18n.t("MEB-IOL-SAB-2024"),
      type: "ARTS",
    },
    {
      value: "MEB-IOL-BEOSAB-2024",
      text: i18n.t("MEB-IOL-BEOSAB-2024"),
      type: "PHYSICAL_EDUCATION",
    },
    {
      value: "MEB-IOL-BTYAB-2024",
      text: i18n.t("MEB-IOL-BTYAB-2024"),
      type: "INFORMATION_TECHNOLOGY",
    },
    {
      value: "MEB-IOL-TSRMAB-2024",
      text: i18n.t("MEB-IOL-TSRMAB-2024"),
      type: "DESIGN",
    },
    {
      value: "MEB-IOL-DAB-2024",
      text: i18n.t("MEB-IOL-DAB-2024"),
      type: "CULTURE_AND_RELIGION_KNOWLEDGE",
    },
    {
      value: "MEB-IOL-YDAB-2024",
      text: i18n.t("MEB-IOL-YDAB-2024"),
      type: "FOREIGN_LANGUAGE",
    },
  ];
};

export const filterSkillVersionCodeOptionList = (
  skillVersionCodeOptionList: any,
  coterieType: string | null,
) => {
  if (!coterieType || coterieType === "DEFAULT_OPTION") return [];

  return skillVersionCodeOptionList.filter(
    (skillVersionCode: any) =>
      skillVersionCode.type === null || skillVersionCode.type === coterieType,
  );
};

export const constructCategoryTreeFromParentCode = (categoryDTOList: any[]) => {
  const nodeMap = new Map();
  const rootNodes: any = [];

  categoryDTOList.forEach((categoryDTO) => {
    const node = {
      ...categoryDTO,
      subCategoryDTOList: [],
    };
    nodeMap.set(buildCategoryKey(categoryDTO), node);
  });

  categoryDTOList.forEach((categoryDTO) => {
    const node = nodeMap.get(buildCategoryKey(categoryDTO));
    const parentKey = buildCategoryParentKey(categoryDTO);

    if (categoryDTO.parentCode && nodeMap.has(parentKey)) {
      const parent = nodeMap.get(parentKey);
      parent.subCategoryDTOList.push(node);
    } else {
      rootNodes.push(node);
    }
  });

  const sortNodesByCode = (nodes: any[]) => {
    nodes.sort((a, b) =>
      a.code.localeCompare(b.code, undefined, { numeric: true }),
    );

    nodes.forEach((node) => {
      if (node.subCategoryDTOList && node.subCategoryDTOList.length > 0) {
        sortNodesByCode(node.subCategoryDTOList);
      }
    });

    return nodes;
  };

  return sortNodesByCode(rootNodes);
};
